<template>
  <v-form v-model="isValid" ref="comboboxFormRef" class="e-tags-input__form" @click="checkClick">
    <v-combobox
      :model-value="value"
      ref="comboboxRef"
      :label="label"
      :rules="rules"
      :messages="errorMessages"
      :menu-icon="''"
      chips
      closable-chips
      multiple
      variant="outlined"
      class="e-tags-input"
      @update:modelValue="onValueUpdate"
      @update:search="onInput"
    >
      <template v-if="detailsText" #details>
        <div>
          {{ detailsText }}
        </div>
      </template>
      <template #chip="{ item, index }">
        <div
          :class="{ invalid: validationFunction && !validationFunction(item.title) }"
          class="e-tags-input__chip"
          @click.stop="startEditChip(index, item.title)"
        >
          {{ item.title }}
          <e-icon name="closeSvg" class="e-tags-input__chip-icon" size="14" @click.stop="removeChip(index)" />
        </div>
      </template>
    </v-combobox>
  </v-form>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { nextTick, ref, watch } from 'vue';
import { EIcon } from '@/core/components/ui';

const props = defineProps<{
  modelValue: string[];
  isValid?: boolean;
  label?: string;
  minHeight?: string;
  validationFunction: (item: string) => boolean;
  rules?: ((value?: string[]) => boolean | string)[];
  errorMessages?: string | string[];
  messages?: string | string[];
  delimiters?: string[];
  detailsText?: string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string[]): void;
}>();

const isValid = useVModel(props, 'isValid');

const comboboxFormRef = ref();

const comboboxRef = ref();

const value = useVModel(props, 'modelValue', emit);

const onValueUpdate = (newValue: string[]) => {
  value.value = newValue.map((v) => v.trim());
};

const validate = () => {
  comboboxFormRef.value?.validate();
};

watch(value, validate, {
  deep: true,
});

const removeChip = (index: number) => {
  value.value.splice(index, 1);
};

const startEditChip = (index: number, chipValue: string) => {
  removeChip(index);

  comboboxRef.value.search = chipValue;

  void nextTick(() => {
    comboboxRef.value?.focus();
  });
};

const checkClick = (e: MouseEvent) => {
  if (e.target === comboboxFormRef.value?.$el && !document.activeElement?.classList?.contains('e-tags-input__chip')) {
    comboboxRef.value?.focus();
  }
};

const onInput = (inputValue: string) => {
  if (props.delimiters) {
    const regexp = new RegExp(props.delimiters.join('|'), 'g');
    let values = inputValue.split(regexp);

    if (values.length > 1) {
      values = values.filter((string) => !!string);

      value.value = [...value.value, ...values];

      comboboxRef.value.search = '';
    }
  }

  const input = comboboxFormRef.value?.querySelector('.v-field__input input');

  if (input) {
    input.size = (Math.floor(inputValue.length * 1.1) || 1) + 1;
  }
};

defineExpose({
  validate,
});
</script>

<style scoped lang="scss">
.e-tags-input {
  pointer-events: none !important;

  * {
    pointer-events: none !important;
  }

  &:deep(.e-tags-input__chip) {
    pointer-events: all !important;

    * {
      pointer-events: all !important;
    }
  }

  :deep(.v-field) {
    padding-right: 0;
  }

  :deep(.v-field__input) {
    display: block;

    height: auto;
    min-height: v-bind(minHeight);
    padding: 12px 8px 12px 12px;

    input {
      max-width: 100%;
    }
  }

  :deep(.v-label.v-field-label) {
    top: 26px;
  }

  :deep(.v-combobox__selection) {
    height: auto;
    padding: 0;
  }

  :deep(.v-combobox__selection--selected) {
    .e-tags-input__chip {
      background-color: $primary-tonal-color;
    }
  }

  &__form {
    cursor: text;
  }

  &__chip {
    display: flex;
    align-items: center;

    max-width: 100%;
    margin: 4px 4px 4px 0;
    padding: 6px 12px 6px 16px;

    font-size: $paragraph-s-font-size;
    color: $text-primary-color;
    word-break: break-word;

    background-color: $primary-light-color;
    border: 1px solid $primary-light-color;
    border-radius: $border-radius-l;
    outline-color: $primary-color;

    cursor: pointer;

    &.invalid {
      background-color: $alert-bg-color;
      border: 1px solid $alert-color;
      outline-color: $alert-color;
    }

    &-icon {
      margin-top: 1px;
      margin-left: $gap-3;

      color: $text-secondary-color;

      cursor: pointer;
      transition: color $transition-duration;

      @include hover {
        color: $text-primary-color;
      }
    }
  }
}
</style>
